import LogOperations from '@modules/husleie-framework/src/static-classes/LogOperations.class';
import { ElectricityProviderData } from '@modules/husleie-framework/src/types/ElectricityProviderData.entity';
import { SessionType, SessionTypeEnum } from '@modules/husleie-framework/src/types/Session.entity';
import {
  UpsellOpportunity,
  UpsellOpportunityEnum,
  UpsellStatusType,
  UpsellStatusEnum,
} from '@modules/husleie-framework/src/types/Upsell.entity';
import { UpsellDepositAccount } from '@modules/husleie-framework/src/types/UpsellDepositAccount.entity';
import {
  UpsellDepositSubscriptionDetails,
  UpsellDepositSubscription,
} from '@modules/husleie-framework/src/types/UpsellDepositSubscription.entity';
import {
  UpsellTenancySubscriptionDetails,
  UpsellTenancySubscription,
} from '@modules/husleie-framework/src/types/UpsellTenancySubscription.entity';
import { UpsellElectricity } from '@modules/husleie-framework/src/types/UpsellElectricity.entity';
import { UpsellKYC } from '@modules/husleie-framework/src/types/UpsellKYC.entity';
import DepositAccountUpsellConversionOperations from '@modules/store/upsell/static-classes/DepositAccountUpsellConversionOperations';
import DepositSubscriptionUpsellConversionOperations from '@modules/store/upsell/static-classes/DepositSubscriptionUpsellConversionOperations';
import TenancySubscriptionUpsellConversionOperations from '@modules/store/upsell/static-classes/TenancySubscriptionUpsellConversionOperations';
import ElectricityUpsellConversionOperations from '@modules/store/upsell/static-classes/ElectricityUpsellConversionOperations';
import type {
  UpsellAPIInterface,
  OpportunitiesGet,
  OpportunitiesGetResponse,
  /* Electricity */
  ElectricityProviderDataGet,
  ElectricityProviderDataGetResponse,
  UpsellElectricityCreate,
  UpsellElectricityCreateResponse,
  UpsellElectricityGet,
  UpsellElectricityGetResponse,
  /* Deposit Subscription */
  DepositSubscriptionCreate,
  DepositSubscriptionCreateResponse,
  DepositSubscriptionGetDetails,
  DepositSubscriptionGetDetailsResponse,
  DepositSubscriptionGet,
  DepositSubscriptionGetResponse,
  CreditCheckGet,
  CreditCheckGetResponse,
  /* Deposit Account */
  DepositAccountGetDetails,
  DepositAccountGetDetailsResponse,
  DepositAccountUpdateKYC,
  DepositAccountUpdateKYCResponse,
  DepositAccountGetSigningURL,
  DepositAccountGetSigningURLResponse,
  DepositAccountGet,
  DepositAccountGetResponse,
  /* Security Deposit */
  SecurityDepositGet,
  SecurityDepositGetReponse,
  SecurityDepositGetDetails,
  SecurityDepositGetDetailsResponse,
  SecurityDepositUpdateKYC,
  SecurityDepositUpdateKYCResponse,
  SecurityDepositGetSigningURL,
  SecurityDepositGetSigningURLResponse,
  /* Tenancy Subscription */
  TenancySubscriptionGet,
  TenancySubscriptionGetResponse,
  TenancySubscriptionGetDetails,
  TenancySubscriptionGetDetailsResponse,
  TenancySubscriptionCreateResponse,
  TenancySubscriptionCreate,
} from '@modules/store/upsell/types/UpsellAPI.interface';
import husleieapi from '@modules/husleie-api';
import {
  UpsellApi as UpsellApiHL,
  SessionStoreApi as SessionStoreApiHL,
  ServiceApi as ServiceApiHL,
  ProductApi as ProductApiHL,
} from '@modules/husleieapi2';
import { ContractApi, ElectricityApi, SessionStoreApi as SessionStoreApiSM } from '@modules/smartmieteapi';
import App from '@modules/husleie-framework/src/static-classes/App.class';
import { UserRoleEnum } from '@modules/husleie-framework/src/types/User.entity';

function convertType(type: SessionType): string {
  if (type === SessionTypeEnum.INSPECTION) {
    return 'inspection_report';
  } else if (type === SessionTypeEnum.CONTRACT) {
    return 'contract';
  }

  LogOperations.error(`API|upsell|session|type ${type} not supported`);
  throw new Error('Unsupported session type');
}

function convertOpportunityStatus(status: string): UpsellStatusType {
  switch (status) {
    case 'open':
      return UpsellStatusEnum.OPEN;
    case 'pending':
      return UpsellStatusEnum.SIGNING;
    case 'canceled':
      return UpsellStatusEnum.CANCELED;
    case 'ordered':
      return UpsellStatusEnum.ORDERED;
    case 'complete':
      return UpsellStatusEnum.COMPLETE;
  }
  LogOperations.error(`API|upsell|session|status ${status} not supported`);
  throw new Error('Unsupported session opportunity upsell status');
}

export default class API implements UpsellAPIInterface {
  /* Base */

  getOpportunities(get: OpportunitiesGet): Promise<OpportunitiesGetResponse> {
    return new Promise((resolve, reject) => {
      const type: string = convertType(get.type);
      const api = App.getMarketIdentifier() !== 'GER' ? new SessionStoreApiHL() : new SessionStoreApiSM();

      let response: Promise<any>;
      if (get.userType === UserRoleEnum.LANDLORD) {
        response = api.LandlordUpsellOpportunities(type, get.uuid);
      } else {
        response = api.TenantUpsellOpportunities(type, get.uuid);
      }
      response
        .then((data: object[]) => {
          const convertedList: (null | UpsellOpportunity)[] = data
            .map((entry: any) => {
              const status: UpsellStatusType = convertOpportunityStatus(entry.status);

              if (entry.type === 'electricity') {
                return { type: UpsellOpportunityEnum.ELECTRICITY, uuid: entry.upsell_id, status };
              } else if (entry.type === 'deposit_subscription') {
                return { type: UpsellOpportunityEnum.DEPOSIT_SUBSCRIPTION, uuid: entry.upsell_id, status };
              } else if (entry.type === 'deposit_account') {
                return { type: UpsellOpportunityEnum.DEPOSIT_ACCOUNT, uuid: entry.upsell_id, status };
              } else if (entry.type === 'tenancy_subscription') {
                return { type: UpsellOpportunityEnum.TENANCY_SUBSCRIPTION, uuid: entry.upsell_id, status };
              } else return null;
            })
            .filter(entry => entry);
          const opportunities: UpsellOpportunity[] = convertedList.filter(entry => !!entry) as UpsellOpportunity[];
          resolve({ opportunities });
        })
        .catch((error: any) => {
          LogOperations.error('API|upsell|base|get opportunities', error);
          reject(error);
        });
    });
  }

  getCreditCheck(get: CreditCheckGet): Promise<CreditCheckGetResponse> {
    return new Promise((resolve, reject) => {
      const api = new ServiceApiHL();
      const body: any = { token: get.token, nationalIdentityNumber: get.nationalIdentityNumber };
      api
        .doCreditCheck({ body })
        .then((data: any) => {
          const approved = data?.result === 'approved';
          resolve({ creditCheck: { approved } });
        })
        .catch((error: any) => {
          LogOperations.error('API|upsell|base|get credit check', error);
          reject(error);
        });
    });
  }

  /* Electricity */

  getElectricityProviderData(get: ElectricityProviderDataGet): Promise<ElectricityProviderDataGetResponse> {
    return new Promise((resolve, reject) => {
      const body: any = ElectricityUpsellConversionOperations.internalTarifDataToApi(
        get.amountOfResidents,
        get.address
      );
      const api = new ElectricityApi();
      api
        .calculateElectricityTariff({ body })
        .then((data: any) => {
          const electricityProviderData: ElectricityProviderData =
            ElectricityUpsellConversionOperations.apiProviderDataToInternal(data);
          resolve({ electricityProviderData });
        })
        .catch((error: any) => {
          LogOperations.error('API|upsell|electricity|get providerData', error);
          reject(error);
        });
    });
  }

  getElectricity(get: UpsellElectricityGet): Promise<null | UpsellElectricityGetResponse> {
    return new Promise((resolve, reject) => {
      const api = new ContractApi();
      api
        .getContractSession(get.contractUuid)
        .then((data: any) => {
          let upsellElectricity: null | UpsellElectricity = null;
          if (data.upsale) {
            const upsellEntry: any = data.upsale.find((upsell: any) => upsell.product === 'electricity_subscription');
            if (upsellEntry) {
              upsellElectricity = ElectricityUpsellConversionOperations.apiToInternal(
                upsellEntry,
                get.userTranslationOptions
              );
            }
          }
          resolve(upsellElectricity ? { upsellElectricity } : null);
        })
        .catch((error: any)=> {
          LogOperations.error('API|upsell|electricity|get', error);
          reject(error);
        });
    });
  }

  createElectricity(create: UpsellElectricityCreate): Promise<UpsellElectricityCreateResponse> {
    return new Promise((resolve, reject) => {
      const body: any = ElectricityUpsellConversionOperations.internalToApi(create.upsellElectricity);

      const api = new ContractApi();
      api
        .contract_upsale(create.contractUuid, { body })
        .then((data: any) => {
          const upsellElectricity: UpsellElectricity = ElectricityUpsellConversionOperations.apiToInternal(
            data,
            create.userTranslationOptions
          );
          resolve({ upsellElectricity });
        })
        .catch((error: any) => {
          LogOperations.error('API|upsell|electricity|create', error);
          reject(error);
        });
    });
  }

  getDepositSubscriptionDetails(get: DepositSubscriptionGetDetails): Promise<DepositSubscriptionGetDetailsResponse> {
    return new Promise((resolve, reject) => {
      const api = new SessionStoreApiHL();

      let response: Promise<any>;
      if (get.userType === UserRoleEnum.LANDLORD) {
        response = api.PrepareLandlordUpsell('contract', get.uuid, 'deposit_subscription');
      } else {
        response = api.PrepareTenantUpsell('contract', get.uuid, 'deposit_subscription');
      }
      response
        .then((data: any) => {
          const upsellPrice: UpsellDepositSubscriptionDetails = {
            monthlyPrice: data.monthlyPrice,
            setupFee: data.setupFee,
            currency: data.currency,
            token: data.token,
          };
          resolve({ upsellPrice });
        })
        .catch((error: any) => {
          LogOperations.error('API|upsell|DepositSubscription|details|get', error);
          reject(error);
        });
    });
  }

  /* Tenancy Subscription */

  getTenancySubscription(get: TenancySubscriptionGet): Promise<TenancySubscriptionGetResponse> {
    return new Promise((resolve, reject) => {
      const api = new UpsellApiHL();
      api
        .getUpsellStatus('tenancy_subscription', get.uuid)
        .then((data: any) => {
          const upsellTenancySubscription: UpsellTenancySubscription =
            TenancySubscriptionUpsellConversionOperations.apiToInternal(data);
          resolve({ upsellTenancySubscription });
        })
        .catch((error: any) => {
          LogOperations.error('API|upsell|TenancySubscription|get', error);
          reject(error);
        });
    });
  }

  getTenancySubscriptionDetails(get: TenancySubscriptionGetDetails): Promise<TenancySubscriptionGetDetailsResponse> {
    return new Promise((resolve, reject) => {
      const api = new SessionStoreApiSM();

      let response: Promise<any>;
      if (get.userType === UserRoleEnum.LANDLORD) {
        response = api.PrepareLandlordUpsell(get.sessionType, get.uuid, 'tenancy_subscription');
      } else {
        response = api.PrepareTenantUpsell(get.sessionType, get.uuid, 'tenancy_subscription');
      }

      response
        .then((data: any) => {
          const upsell: UpsellTenancySubscriptionDetails = {
            currency: data.currency,
            includedVat: data.includedVat,
            monthlyPrice: data.monthlyPrice,
            plans: data.plans,
            token: data.token,
            vat: data.vat,
          };
          resolve({ upsell });
        })
        .catch((error: any) => {
          LogOperations.error('API|upsell|TenancySubscription|details|get', error);
          reject(error);
        });
    });
  }

  createTenancySubscription(create: TenancySubscriptionCreate): Promise<TenancySubscriptionCreateResponse> {
    return new Promise((resolve, reject) => {
      const api = new UpsellApiHL();
      const body: TenancySubscriptionCreate = create;

      api
        .createUpsell('tenancy_subscription', { body })
        .then((data: any) => {
          resolve({ upsellId: data.id });
        })
        .catch((error: any) => {
          LogOperations.error('API|upsell|TenancySubscription|create', error);
          reject(error);
        });
    });
  }

  createDepositSubscription(create: DepositSubscriptionCreate): Promise<DepositSubscriptionCreateResponse> {
    return new Promise((resolve, reject) => {
      const api = new UpsellApiHL();
      const body: any = { token: create.token };

      api
        .createUpsell('deposit_subscription', { body })
        .then((data: any) => {
          resolve({ upsellId: data.id });
        })
        .catch((error: any) => {
          LogOperations.error('API|upsell|DepositSubscription|create', error);
          reject(error);
        });
    });
  }

  getDepositSubscription(get: DepositSubscriptionGet): Promise<DepositSubscriptionGetResponse> {
    return new Promise((resolve, reject) => {
      const api = new UpsellApiHL();
      api
        .getUpsellStatus('deposit_subscription', get.uuid)
        .then((data: any) => {
          const upsellDepositSubscription: UpsellDepositSubscription =
            DepositSubscriptionUpsellConversionOperations.apiToInternal(data);

          resolve({ upsellDepositSubscription });
        })
        .catch((error: any) => {
          LogOperations.error('API|upsell|DepositSubscription|get', error);
          reject(error);
        });
    });
  }

  /* Deposit Account */

  getDepositAccountDetails(get: DepositAccountGetDetails): Promise<DepositAccountGetDetailsResponse> {
    return new Promise((resolve, reject) => {
      const api = new SessionStoreApiHL();

      let response: Promise<any>;
      if (get.userType === UserRoleEnum.LANDLORD) {
        response = api.PrepareLandlordUpsell('contract', get.uuid, 'deposit_account');
      } else {
        response = api.PrepareTenantUpsell('contract', get.uuid, 'deposit_account');
      }
      response
        .then((data: any) => {
          const kyc: undefined | UpsellKYC = DepositAccountUpsellConversionOperations.apiKYCToInternal(data.kyc);
          resolve({ uuid: data.id, kyc });
        })
        .catch((error: any) => {
          LogOperations.error('API|upsell|DepositAccount|KYC|get', error);
          reject(error);
        });
    });
  }

  updateDepositAccountKYC(update: DepositAccountUpdateKYC): Promise<DepositAccountUpdateKYCResponse> {
    return new Promise((resolve, reject) => {
      const api = new ProductApiHL();
      const body: any = DepositAccountUpsellConversionOperations.internalKYCToApi(update.kyc);

      api
        // @ts-ignore
        .postDepositAccountKCYForm(update.uuid, { body })
        .then(() => {
          resolve({});
        })
        .catch((error: any) => {
          LogOperations.error('API|upsell|DepositAccount|KYC|update', error);
          reject(error);
        });
    });
  }

  getDepositAccountSigningURL(get: DepositAccountGetSigningURL): Promise<DepositAccountGetSigningURLResponse> {
    return new Promise((resolve, reject) => {
      const api = new ProductApiHL();

      api
        .getDepositAccountSigningUrl(get.uuid)
        .then((data: any) => {
          resolve({ signingURL: data.signingUrl });
        })
        .catch((error: any) => {
          LogOperations.error('API|upsell|DepositAccount|Signing URL|get', error);
          reject(error);
        });
    });
  }

  getDepositAccountLegacy(get: DepositAccountGet): Promise<DepositAccountGetResponse> {
    return new Promise((resolve, reject) => {
      const hlApi = new husleieapi();
      hlApi.call('ProductApi.getDepositAccount', get.uuid, (error: any, data: any, response: any) => {
        if (error) {
          LogOperations.error('API|upsell|DepositAccount|get', response.text.error.message);
          reject(error);
        } else {
          const upsellDepositAccount: UpsellDepositAccount = DepositAccountUpsellConversionOperations.apiToInternal({
            ...response.body.data,
            status: response.body.status,
            kyc: response.body.kyc,
          });

          resolve({ upsellDepositAccount });
        }
      });
    });
  }

  getDepositAccount(get: DepositAccountGet): Promise<DepositAccountGetResponse> {
    return new Promise((resolve, reject) => {
      const api = new UpsellApiHL();
      api
        .getUpsellStatus('deposit_account', get.uuid)
        .then((data: any) => {
          const upsellDepositAccount: UpsellDepositAccount =
            DepositAccountUpsellConversionOperations.apiToInternal(data);

          resolve({ upsellDepositAccount });
        })
        .catch((error: any) => {
          LogOperations.error('API|upsell|DepositAccount|get', error);
          reject(error);
        });
    });
  }

  /* Security Deposit */

  getSecurityDeposit(get: SecurityDepositGet): Promise<SecurityDepositGetReponse> {
    return new Promise((resolve, reject) => {
      const api = new ProductApiHL();
      api
        .getSecurityDeposit(get.uuid)
        .then((data: any) => {
          resolve({ upsellSecurityDeposit: {
            uuid: data.id,
            data: data.data,
            status: data.status,
            kyc: data.kyc,
            hasDueDateGuaranteeUpsale: data.hasDueDateGuaranteeUpsale,
          }});
        })
        .catch((error: any) => {
          LogOperations.error('API|upsell|SecurityDeposit|details|get', error);
          reject(error);
        });
    });
  }

  getSecurityDepositDetails(get: SecurityDepositGetDetails): Promise<SecurityDepositGetDetailsResponse> {
    return new Promise((resolve, reject) => {
      const api = new ProductApiHL();

      api
        .getSecurityDeposit(get.uuid)
        .then((data: any) => {
          resolve({
            uuid: data.id,
            data: data.data,
            status: data.status,
            kyc: data.kyc,
          });
        })
        .catch((error: any) => {
          LogOperations.error('API|upsell|SecurityDeposit|details|get', error);
          reject(error);
        });
    });
  }

  updateSecurityDepositKYC(update: SecurityDepositUpdateKYC): Promise<SecurityDepositUpdateKYCResponse> {
    return new Promise((resolve, reject) => {
      const api = new ProductApiHL();
      const body: any = { kyc: update.kyc };

      api
        // @ts-ignore
        .postSecurityDepositKCYForm(update.uuid, { body })
        .then(() => {
          resolve({});
        })
        .catch((error: any) => {
          LogOperations.error('API|upsell|SecurityDeposit|KYC|update', error);
          reject(error);
        });
    });
  }

  getSecurityDepositSigningURL(get: SecurityDepositGetSigningURL): Promise<SecurityDepositGetSigningURLResponse> {
    return new Promise((resolve, reject) => {
      const api = new ProductApiHL();
      api
        .getSecurityDepositSigningUrl(get.uuid, {
          query: {
            role: get.role?.toLowerCase(),
          },
        })
        .then((data: any) => {
          resolve({
            signingURL: data.signingUrl,
            redirectURL: data.redirectUrl,
            dueDateGuaranteeDocumentUrl: data.dueDateGuaranteeDocumentUrl,
          });
        })
        .catch((error: any) => {
          LogOperations.error('API|upsell|SecurityDeposit|Signing URL|get', error);
          reject(error);
        });
    });
  }
}
